import { API_BASE_URL } from '../../../api/config';
import { createFileLogger } from '../../../utils/logger';
import { GoogleDriveErrorHandler } from './GoogleDriveErrorHandler';

const log = createFileLogger('GOOGLE_DRIVE_ADAPTER', {enabled: false});

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
  iconLink?: string;
  thumbnailLink?: string;
  createdTime?: string;
  modifiedTime?: string;
  parents?: string[];
  shared?: boolean;
  ownedByMe?: boolean;
  size?: number;
}

export interface DriveSearchResult {
  ok: boolean;
  results: DriveFile[];
  totalCount: number;
}

export interface DriveFileContent {
  ok: boolean;
  fileId: string;
  fileName: string;
  mimeType: string;
  contentType: 'markdown' | 'json' | 'text';
  content: string | object;
  metadata: {
    id: string;
    name: string;
    size?: number;
    modifiedTime: string;
  };
}

export interface ImportResult {
  ok: boolean;
  entityId: string;
  entityType: string;
  message: string;
}

export interface ConvertResult {
  ok: boolean;
  entityId: string;
  entityType: string;
  conversionType: 'note' | 'sheet' | 'reference';
  fileName: string;
  message: string;
}

export interface DriveOAuthCallbackData {
  tokens: string;
  accountId: string;
  accountName: string;
  profile: any;
}

export class GoogleDriveAdapter {
  private baseUrl = `${API_BASE_URL}/integrations/google-drive`;
  private activeOAuthCleanup: (() => void) | null = null;

  private getAuthHeaders() {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      throw new Error('No auth token found');
    }
    return {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    };
  }

  async getAuthorizationUrl(): Promise<string> {
    try {
      log.log('[GoogleDriveAdapter] Getting OAuth URL');
      
      const response = await fetch(`${this.baseUrl}/oauth-url`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get OAuth URL');
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] OAuth URL received');
      
      return data.authorization_url;
    } catch (error) {
      GoogleDriveErrorHandler.logError('getAuthorizationUrl', error);
      throw error;
    }
  }


  async searchFiles(
    integrationId: string,
    query: string,
    filters?: {
      mimeType?: string;
      folderId?: string;
      modifiedAfter?: string;
      ownedByMe?: boolean;
      limit?: number;
    }
  ): Promise<DriveSearchResult> {
    try {
      log.log('[GoogleDriveAdapter] Searching Drive:', { query, filters });
      
      const response = await fetch(`${this.baseUrl}/search`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          query,
          filters
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Search failed'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] Search complete:', data.results.length, 'results');
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('searchFiles', error, { integrationId, query });
      throw error;
    }
  }

  async readFileContent(
    integrationId: string,
    fileId: string,
    outputFormat?: 'auto' | 'markdown' | 'json' | 'text'
  ): Promise<DriveFileContent> {
    try {
      log.log('[GoogleDriveAdapter] Reading file content:', fileId);
      
      const response = await fetch(`${this.baseUrl}/read-file`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          file_id: fileId,
          output_format: outputFormat
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Failed to read file'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] File content read successfully');
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('readFileContent', error, { integrationId, fileId });
      throw error;
    }
  }

  async importFile(
    integrationId: string,
    fileId: string,
    spaceId: string,
    orgId?: string
  ): Promise<ImportResult> {
    try {
      log.log('[GoogleDriveAdapter] Importing file:', fileId);
      
      const response = await fetch(`${this.baseUrl}/import-file`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          file_id: fileId,
          space_id: spaceId,
          org_id: orgId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Import failed'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] File imported as entity:', data.entityId);
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('importFile', error, { integrationId, fileId, spaceId });
      throw error;
    }
  }

  async convertFile(
    integrationId: string,
    fileId: string,
    spaceId: string,
    orgId?: string
  ): Promise<ConvertResult> {
    try {
      log.log('[GoogleDriveAdapter] Converting file:', fileId);

      const response = await fetch(`${this.baseUrl}/convert-file`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          file_id: fileId,
          space_id: spaceId,
          org_id: orgId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Convert failed'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] File converted as entity:', data.entityId, `(${data.conversionType})`);

      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('convertFile', error, { integrationId, fileId, spaceId });
      throw error;
    }
  }

  async revokeAccess(integrationId: string): Promise<void> {
    try {
      log.log('[GoogleDriveAdapter] Revoking access for integration:', integrationId);
      
      const response = await fetch(`${this.baseUrl}/revoke`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ integration_id: integrationId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Failed to revoke access'), { status: response.status });
      }

      log.log('[GoogleDriveAdapter] Access revoked successfully');
    } catch (error) {
      GoogleDriveErrorHandler.logError('revokeAccess', error, { integrationId });
      throw error;
    }
  }

  async initiateOAuthFlow(): Promise<DriveOAuthCallbackData> {
    if (this.activeOAuthCleanup) {
      log.warn('[GoogleDriveAdapter] OAuth flow already in progress');
      throw new Error('OAuth flow already in progress');
    }

    return new Promise(async (resolve, reject) => {
      let popup: Window | null = null;
      let checkClosed: NodeJS.Timeout | null = null;
      let timeout: NodeJS.Timeout | null = null;
      
      const cleanup = () => {
        if (checkClosed) {
          clearInterval(checkClosed);
          checkClosed = null;
        }
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        window.removeEventListener('message', handleMessage);
        try {
          if (popup && !popup.closed) {
            popup.close();
          }
        } catch (e) {
          // Popup might already be closed or inaccessible due to COOP
        }
        this.activeOAuthCleanup = null;
      };

      this.activeOAuthCleanup = cleanup;

      const handleMessage = (event: MessageEvent) => {
        log.log('[GoogleDriveAdapter] 📨 Message received:', {
          origin: event.origin,
          expectedOrigin: window.location.origin,
          type: event.data?.type,
          hasData: !!event.data?.data
        });
        
        if (event.origin !== window.location.origin) {
          log.warn('[GoogleDriveAdapter] ⚠️ Message from wrong origin, ignoring');
          return;
        }

        if (event.data.type === 'GOOGLE_DRIVE_AUTH_SUCCESS') {
          log.log('[GoogleDriveAdapter] ✅ OAuth successful, received data');
          cleanup();
          resolve(event.data.data);
        } else if (event.data.type === 'GOOGLE_DRIVE_AUTH_ERROR') {
          log.error('[GoogleDriveAdapter] ❌ OAuth error:', event.data.error);
          cleanup();
          reject(new Error(event.data.error || 'OAuth failed'));
        }
      };

      try {
        const authUrl = await this.getAuthorizationUrl();
        
        log.log('[GoogleDriveAdapter] 🔗 Opening OAuth popup');
        
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        popup = window.open(
          authUrl,
          'GoogleDriveAuth',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popup) {
          cleanup();
          reject(new Error('Failed to open popup window. Please allow popups for this site.'));
          return;
        }
        
        log.log('[GoogleDriveAdapter] ✅ Popup opened successfully');

        window.addEventListener('message', handleMessage);

        // ✅ FIX BUG #18: Detect COOP and stop checking if we can't access popup
        let coopDetected = false;
        checkClosed = setInterval(() => {
          try {
            if (popup && popup.closed) {
              cleanup();
              reject(new Error('OAuth cancelled by user'));
            }
          } catch (e) {
            // Cross-Origin-Opener-Policy prevents checking popup.closed
            // Clear interval and rely solely on postMessage or timeout
            if (!coopDetected) {
              coopDetected = true;
              log.log('[GoogleDriveAdapter] COOP detected, relying on postMessage only');
              if (checkClosed) {
                clearInterval(checkClosed);
                checkClosed = null;
              }
            }
          }
        }, 1000);

        // Timeout after 5 minutes (Google OAuth can be slow)
        timeout = setTimeout(() => {
          cleanup();
          reject(new Error('OAuth timeout - please try again'));
        }, 5 * 60 * 1000);

      } catch (error) {
        log.error('[GoogleDriveAdapter] Error initiating OAuth:', error);
        cleanup();
        reject(error);
      }
    });
  }

  async readGoogleDoc(integrationId: string, documentId: string): Promise<any> {
    try {
      log.log('[GoogleDriveAdapter] Reading Google Doc:', documentId);
      
      const response = await fetch(`${API_BASE_URL}/google-docs/read`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          document_id: documentId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Failed to read Google Doc'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] Successfully read Google Doc');
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('readGoogleDoc', error, { integrationId, documentId });
      throw error;
    }
  }

  async writeGoogleDoc(integrationId: string, documentId: string, requests: any[]): Promise<any> {
    try {
      log.log('[GoogleDriveAdapter] Writing to Google Doc:', documentId);
      
      const response = await fetch(`${API_BASE_URL}/google-docs/write`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          document_id: documentId,
          requests: requests
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Failed to write to Google Doc'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] Successfully wrote to Google Doc');
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('writeGoogleDoc', error, { integrationId, documentId });
      throw error;
    }
  }

  async readGoogleSheet(integrationId: string, spreadsheetId: string, range: string): Promise<any> {
    try {
      log.log('[GoogleDriveAdapter] Reading Google Sheet:', spreadsheetId, 'range:', range);
      
      const response = await fetch(`${API_BASE_URL}/google-sheets/read`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          spreadsheet_id: spreadsheetId,
          range: range
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Failed to read Google Sheet'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] Successfully read Google Sheet');
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('readGoogleSheet', error, { integrationId, spreadsheetId, range });
      throw error;
    }
  }

  async writeGoogleSheet(integrationId: string, spreadsheetId: string, range: string, values: any[][]): Promise<any> {
    try {
      log.log('[GoogleDriveAdapter] Writing to Google Sheet:', spreadsheetId, 'range:', range);
      
      const response = await fetch(`${API_BASE_URL}/google-sheets/write`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          spreadsheet_id: spreadsheetId,
          range: range,
          values: values
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw Object.assign(new Error(error.detail || 'Failed to write to Google Sheet'), { status: response.status });
      }

      const data = await response.json();
      log.log('[GoogleDriveAdapter] Successfully wrote to Google Sheet');
      
      return data;
    } catch (error) {
      GoogleDriveErrorHandler.logError('writeGoogleSheet', error, { integrationId, spreadsheetId, range });
      throw error;
    }
  }
}

export const googleDriveAdapter = new GoogleDriveAdapter();
